import { useState } from 'react';
import '../tailwind.css';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Reporting/ui/card';
import ReportForm from '../../../components/Reporting/report-form';
import PromotionReportOutput from '../../../components/Reporting/promotion-report-output';
import { HeaderMenu } from '../../../components/Reporting/header-menu';
import { ReportLoading } from '../../../components/Reporting/report-loading';
import PagePrintPreview from '../../../components/Reporting/page-print-preview';
import instance from '../../../services/api';
import { toast } from 'react-toastify';
import { TicketPercent } from 'lucide-react';

const PAGE_SIZE = 20;

export default function PromotionReportPage() {
  const [report, setReport] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterParams, setFilterParams] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);
  const [showPagePreview, setShowPagePreview] = useState(false);

  const buildBody = (data, page) => ({
    businessUuid: data.businessUuid,
    businessId: data.businessId,
    startDate: data.startDate,
    endDate: data.endDate,
    startTime: data.startTime,
    endTime: data.endTime,
    useLocalTime: data.useLocalTime || false,
    pageSize: PAGE_SIZE,
    page,
  });

  const resetReport = () => {
    setReport(null);
    setDetailItems([]);
    setDetailTotal(0);
    setHasMoreData(false);
    setCurrentPage(0);
  };

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    setFilterParams(data);

    try {
      const { data: response } = await instance.post(
        '/reports/promotion',
        buildBody(data, 0)
      );

      setReport(response);
      setDetailItems(response.detail?.items || []);
      setDetailTotal(response.detail?.total || 0);
      setHasMoreData(response.detail?.hasNextPage || false);
      setCurrentPage(0);

      toast.success('Promotion Report Generated Successfully');

      setTimeout(() => {
        document
          .getElementById('promotion-report')
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to generate promotion report. Please try again.';
      toast.error(errorMessage);
      resetReport();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!filterParams || isLoadingMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const { data: response } = await instance.post(
        '/reports/promotion/detail',
        buildBody(filterParams, nextPage)
      );

      const newItems = response.items || [];
      setDetailItems((prev) => [...prev, ...newItems]);
      setHasMoreData(response.hasNextPage || false);
      setCurrentPage(nextPage);

      toast.success(`Loaded ${newItems.length} more usages`);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to load more usages. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  /**
   * The detail table only holds the pages the user has pulled in, so printing
   * from it would file a report that stops mid-history. Drain the remaining
   * pages first and print the whole thing.
   */
  const handlePrint = async () => {
    if (!filterParams || isPreparingPrint) return;

    if (!hasMoreData) {
      setShowPagePreview(true);
      return;
    }

    setIsPreparingPrint(true);
    try {
      const collected = [...detailItems];
      let page = currentPage;
      let more = true;

      while (more) {
        page += 1;
        const { data: response } = await instance.post(
          '/reports/promotion/detail',
          buildBody(filterParams, page)
        );
        const items = response.items || [];
        collected.push(...items);
        // An empty page with hasNextPage still set would loop forever.
        more = (response.hasNextPage || false) && items.length > 0;
      }

      setDetailItems(collected);
      setCurrentPage(page);
      setHasMoreData(false);
      setShowPagePreview(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to load all usages for printing. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsPreparingPrint(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderMenu currentPage="promotions" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="no-print mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TicketPercent className="text-primary mr-3 h-6 w-6" />
              Generate Promotion Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportForm
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              setReportData={(data) => {
                if (!data) resetReport();
              }}
              showProviders={false}
              showSections={false}
            />
          </CardContent>
        </Card>

        {isLoading ? (
          <div id="promotion-report-loading">
            <ReportLoading />
          </div>
        ) : (
          report && (
            <PromotionReportOutput
              report={report}
              detailItems={detailItems}
              detailTotal={detailTotal}
              isLoadingMore={isLoadingMore}
              hasMoreData={hasMoreData}
              onLoadMore={handleLoadMore}
              onPrint={handlePrint}
              isPreparingPrint={isPreparingPrint}
            />
          )
        )}
      </div>

      {showPagePreview && report && (
        <PagePrintPreview
          // The preview names the file from `dateRange`, which only the POS
          // performance payload carries; ours keeps the range under `filters`.
          data={{
            ...report,
            dateRange: {
              startDate: report.filters?.startDate,
              endDate: report.filters?.endDate,
            },
          }}
          contentSelector="#promotion-report"
          reportType="Promotion-Report"
          onClose={() => setShowPagePreview(false)}
        />
      )}
    </div>
  );
}
